/**
 * Gestor de estado central del juego: respeto, dinero, salud, inventario
 * (yerba/comida), flags, misiones completadas y racha. Singleton accesible
 * desde cualquier escena. Autoguarda tras cada cambio relevante y emite
 * eventos para que el HUD (y cualquier otra escena) reaccione sin acoplarse
 * al motor de guardado.
 *
 * No sabe nada de historia: solo mueve números y flags genéricos.
 */

import Phaser from 'phaser';
import type { SaveGame, StatKey } from '@/data/types';
import { loadSave, persistSave } from './storage';

export const SAVE_VERSION = 1;
/** Evento emitido cada vez que cambia una stat visible en el HUD. */
export const STAT_CHANGED_EVENT = 'stat-changed';
/** Evento emitido una vez el save inicial ha terminado de cargar. */
export const SAVE_READY_EVENT = 'save-ready';

function crearSaveInicial(): SaveGame {
  return {
    playableCharacterId: 'sdowp',
    respeto: 0,
    dinero: 0,
    salud: 100,
    inventario: { yerba: 0, comida: 0 },
    flags: {},
    misionesCompletadas: [],
    minijuegosJugados: {},
    zonasDesbloqueadas: ['la-casa', 'interior-casa', 'campo-futbol', 'el-monte', 'el-barrio'],
    racha: { diasConsecutivos: 0, ultimaFechaCompletada: '' },
    version: SAVE_VERSION,
  };
}

/** Migra saves de versiones antiguas a la actual. Placeholder para v1. */
function migrar(save: SaveGame): SaveGame {
  if (save.version === SAVE_VERSION) return save;
  if (save.version > SAVE_VERSION) {
    // Save con version futura (bundle viejo cacheado contra datos nuevos).
    // Devolver TAL CUAL: nunca estampar version hacia abajo, que perdería
    // información y rompería futuras migraciones reales.
    console.warn(
      `[GameState] save con version futura (${save.version} > ${SAVE_VERSION}), usando tal cual`,
    );
    return save;
  }
  // version < SAVE_VERSION: migrar hacia arriba (placeholder en v1).
  return { ...save, version: SAVE_VERSION };
}

class GameStateManager extends Phaser.Events.EventEmitter {
  private save: SaveGame = crearSaveInicial();
  private ready = false;
  private saving: Promise<void> = Promise.resolve();

  /** Carga el save persistido (si existe) antes de que arranque el juego. */
  async init(): Promise<void> {
    const loaded = await loadSave();
    this.save = loaded ? migrar(loaded) : crearSaveInicial();
    this.ready = true;
    // Emitimos un clon para que los suscriptores no puedan mutar el estado
    // vivo saltándose autosave/eventos.
    this.emit(SAVE_READY_EVENT, structuredClone(this.save));
  }

  isReady(): boolean {
    return this.ready;
  }

  private autosave(stat: StatKey): void {
    // Encadena el guardado para no disparar escrituras concurrentes.
    this.saving = this.saving.then(() => persistSave(this.save)).catch((err) => {
      console.error('[GameState] fallo autoguardando', err);
    });
    this.emit(STAT_CHANGED_EVENT, stat, this.getSnapshot());
  }

  getSnapshot(): Readonly<SaveGame> {
    // structuredClone para que nadie mute el estado vivo saltándose
    // autosave/eventos desde fuera del manager.
    return structuredClone(this.save) as Readonly<SaveGame>;
  }

  // --- Respeto ---
  get respeto(): number {
    return this.save.respeto;
  }

  addRespeto(cantidad: number): void {
    this.save.respeto = Math.max(0, this.save.respeto + cantidad);
    this.autosave('respeto');
  }

  // --- Dinero ---
  get dinero(): number {
    return this.save.dinero;
  }

  addDinero(cantidad: number): void {
    this.save.dinero = Math.max(0, this.save.dinero + cantidad);
    this.autosave('dinero');
  }

  /** Intenta gastar dinero. Devuelve false (sin cambios) si no hay saldo. */
  gastarDinero(cantidad: number): boolean {
    if (this.save.dinero < cantidad) return false;
    this.save.dinero -= cantidad;
    this.autosave('dinero');
    return true;
  }

  // --- Salud ---
  get salud(): number {
    return this.save.salud;
  }

  addSalud(cantidad: number): void {
    this.save.salud = Phaser.Math.Clamp(this.save.salud + cantidad, 0, 100);
    this.autosave('salud');
  }

  // --- Inventario (yerba/comida) ---
  get inventario() {
    return this.save.inventario;
  }

  addYerba(cantidad: number): void {
    this.save.inventario.yerba = Math.max(0, this.save.inventario.yerba + cantidad);
    this.autosave('inventario');
  }

  addComida(cantidad: number): void {
    this.save.inventario.comida = Math.max(0, this.save.inventario.comida + cantidad);
    this.autosave('inventario');
  }

  // --- Flags ---
  getFlag(id: string): boolean {
    return !!this.save.flags[id];
  }

  setFlag(id: string, valor = true): void {
    this.save.flags[id] = valor;
    this.autosave('respeto'); // los flags no tienen stat propio en el HUD; reutiliza el canal
  }

  // --- Misiones ---
  marcarMisionCompletada(missionId: string): void {
    if (!this.save.misionesCompletadas.includes(missionId)) {
      this.save.misionesCompletadas.push(missionId);
      this.autosave('respeto');
    }
  }

  misionCompletada(missionId: string): boolean {
    return this.save.misionesCompletadas.includes(missionId);
  }

  // --- Minijuegos ---
  registrarPartida(minigameId: string): void {
    this.save.minijuegosJugados[minigameId] = (this.save.minijuegosJugados[minigameId] ?? 0) + 1;
    this.autosave('respeto');
  }

  // --- Racha ---
  get racha() {
    return this.save.racha;
  }

  registrarDiaCompletado(fechaIsoHoy: string): void {
    // Guard de mismo día: si ya registramos hoy, no incrementamos la racha.
    if (this.save.racha.ultimaFechaCompletada === fechaIsoHoy) return;
    this.save.racha.diasConsecutivos += 1;
    this.save.racha.ultimaFechaCompletada = fechaIsoHoy;
    this.autosave('racha');
  }

  reiniciarRacha(): void {
    this.save.racha.diasConsecutivos = 0;
    this.autosave('racha');
  }

  // --- Personaje jugable (preparado para v2) ---
  get playableCharacterId(): string {
    return this.save.playableCharacterId;
  }

  setPlayableCharacter(characterId: string): void {
    this.save.playableCharacterId = characterId;
    this.autosave('respeto');
  }

  /** Reinicia la partida a cero (borra el progreso). Uso: debug / testing. */
  reset(): void {
    this.save = crearSaveInicial();
    this.autosave('respeto');
  }
}

/** Instancia única compartida por todas las escenas. */
export const GameState = new GameStateManager();
